import { Element, Path } from 'slate';
import type { Editor, Point, Range } from 'slate';

import type { ClipRect } from './draw-instruction';
import type { RenderNodeFn } from './render-context';
import type {
  AlineSelectionIntersection,
  GridSelectionInfo,
  NodeBoundingBox,
  RemoteSelectionIntersection,
  RenderModelPage,
  SuggestedEdit,
} from '../../slate-api';
import { Queries } from '../../slate-api';
import type { DocumentCommentsById } from '../../types';
import type { SpellcheckRangeRef } from '../spellcheck';

/**
 * Render options with all properties set to inactive values. Use this when the majority of these
 * options are non-applicable, such as when generating PDFs or in tests.
 */
export const nullRenderOptions: Omit<RenderOptions, 'rMPath'> = {
  activeCommentId: null,
  activeSearchHit: null,
  canExpandAndCollapseDynamicBlocks: false,
  checkboxMaxRequiredByFieldId: new Map(),
  commentsById: new Map(),
  currentUserId: null,
  dynamicBlockColors: [],
  gridSelectionInfo: null,
  isFocused: false,
  isPDFExport: false,
  isReadOnly: true,
  matchAccents: false,
  matchCase: false,
  rMDropPoint: null,
  rMRemoteSelIntersections: null,
  rMSelIntersection: null,
  search: null,
  showNonPrintingCharacters: false,
  showPageBreaks: false,
  spellcheckRangesByParagraph: null,
  spellcheckRetiredFromByParagraph: null,
};

/**
 * Render node state properties that don't need to be specified since they have default values.
 */
const defaultRenderNodeState = {
  clipRect: null,
  isLastNodeInLine: null,
  offsetX: 0,
  offsetY: 0,
} satisfies Partial<RenderNodeState>;

export interface RenderOptions
  extends StaticRenderingData, Omit<RenderNodeState, keyof typeof defaultRenderNodeState> {
  /** For testing */
  overrideRenderPage?: RenderNodeFn<RenderModelPage, BaseRenderNodeOptions>;
}

export interface BaseRenderNodeOptions {
  /**
   * Whether some parent element is rendering a selection highlight. If so, child nodes shouldn't
   * render their own selection highlights.
   */
  selectionHighlightAbove: boolean;
  inheritDeleteEdit?: SuggestedEdit;
  inheritInsertEdit?: SuggestedEdit;
}

/**
 * Data used for rendering that is constant for all nodes in a rendering pass.
 */
export interface StaticRenderingData {
  activeCommentId: string | null;
  activeSearchHit: Range | null;
  canExpandAndCollapseDynamicBlocks: boolean;
  checkboxMaxRequiredByFieldId: ReadonlyMap<string, number | null>;
  commentsById: DocumentCommentsById;
  currentUserId: string | null;
  dynamicBlockColors: string[];
  gridSelectionInfo: GridSelectionInfo | null;
  isFocused: boolean;
  isPDFExport: boolean;
  isReadOnly: boolean;
  matchAccents: boolean;
  matchCase: boolean;
  search: string | null;
  showNonPrintingCharacters: boolean;
  showPageBreaks: boolean;
  /**
   * Content-model range refs to redline, keyed by paragraph path (`path.join(',')`) so each
   * paragraph converts only its own. Null unless `SpellcheckRunner` populates it. Unwrap
   * `.current` at paint time; the ref object stays stable between passes.
   */
  spellcheckRangesByParagraph: ReadonlyMap<string, readonly SpellcheckRangeRef[]> | null;
  /**
   * Per-paragraph paint cutoff while the user types. The range-ref list stays put; only this
   * map changes. A paragraph reads its own key so a keystroke does not redraw the others.
   */
  spellcheckRetiredFromByParagraph: ReadonlyMap<string, Point | null> | null;
}

/**
 * Data used for rendering a single node.
 */
export interface RenderNodeState {
  /** The rectangle used to clip content currently being rendered. */
  clipRect: ClipRect | null;
  /**
   * Whether this node is part of the last subtree of the ancestor line, or null for nodes with no
   * ancestor line.
   */
  isLastNodeInLine: boolean | null;
  /** The horizontal offset of the node currently being rendered. */
  offsetX: number;
  /** The vertical offset of the node currently being rendered. */
  offsetY: number;
  /** The position of the drop caret, if it's inside the node currently being rendered. */
  rMDropPoint: Point | null;
  /** The path of the node currently being rendered. */
  rMPath: Path | null;
  /** The intersection of any remote selections with the node currently being rendered. */
  rMRemoteSelIntersections: Record<string, RemoteSelectionIntersection> | null;
  /** The intersection of the selection with the node currently being rendered. */
  rMSelIntersection: AlineSelectionIntersection | null;
}

/**
 * Split a combined options object into static data and render node state. This reduces coupling
 * since callers don't need to know whether any given option is static or node-specific.
 */
export function getStaticRenderingDataAndInitialRenderNodeState(options: RenderOptions): {
  initialRenderNodeState: RenderNodeState;
  staticRenderingData: StaticRenderingData;
} {
  const {
    overrideRenderPage,
    rMDropPoint,
    rMPath,
    rMRemoteSelIntersections,
    rMSelIntersection,
    ...staticRenderingData
  } = options;

  return {
    initialRenderNodeState: {
      rMDropPoint,
      rMPath,
      rMRemoteSelIntersections,
      rMSelIntersection,
      ...defaultRenderNodeState,
    },
    staticRenderingData,
  };
}

/**
 * Transform the render node state for a given child node.
 */
export function getRenderChildState(
  editor: Editor,
  parentRenderNodeState: RenderNodeState,
  options: { boundingBox: NodeBoundingBox; isLastChild: boolean; parentIsLine: boolean }
): RenderNodeState {
  const { boundingBox, isLastChild, parentIsLine } = options;
  const { leftPx, rMNodeEntry, topPx } = boundingBox;
  const [rMNode, rMPath] = rMNodeEntry;

  /**
   * All render model elements should have descendants that include at least one text node (as per
   * Slate's core requirements). However, if we encounter an invalid child that has no children,
   * avoid calling Editor.range since this will throw an error.
   *
   * This does not prevent errors caused by other code that may call Editor.range on a childless
   * element, such as RenderContext's rMRange getter or its dependents. If you encounter errors
   * related to this, you should update that code to ensure an element is valid before attempting to
   * get its range.
   */
  const isMissingChildren = Element.isAncestor(rMNode) && !rMNode.children.length;

  const rMRange = isMissingChildren ? null : Queries.renderModelRange(editor, rMPath);

  const rMSelIntersection =
    rMRange &&
    parentRenderNodeState.rMSelIntersection &&
    Queries.getSelectionIntersection(parentRenderNodeState.rMSelIntersection, rMRange);

  const rMRemoteSelIntersections =
    rMRange &&
    Queries.getSelectionIntersections(parentRenderNodeState.rMRemoteSelIntersections, rMRange);

  const rMDropPoint =
    parentRenderNodeState.rMDropPoint &&
    (Path.isAncestor(rMPath, parentRenderNodeState.rMDropPoint.path) ||
      Path.equals(rMPath, parentRenderNodeState.rMDropPoint.path))
      ? parentRenderNodeState.rMDropPoint
      : null;

  return {
    clipRect: parentRenderNodeState.clipRect,
    isLastNodeInLine: (parentRenderNodeState.isLastNodeInLine || parentIsLine) && isLastChild,
    offsetX: parentRenderNodeState.offsetX + leftPx,
    offsetY: parentRenderNodeState.offsetY + topPx,
    rMDropPoint,
    rMPath,
    rMRemoteSelIntersections,
    rMSelIntersection,
  };
}
